// n8n Webhook Configuration
console.log('[gemini.js] Environment REACT_APP_N8N_WEBHOOK_URL:', process.env.REACT_APP_N8N_WEBHOOK_URL);

const N8N_WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL || 'https://n8n.dleaf.net/webhook/ai-gen-forensic-v2';

console.log('[gemini.js] Using URL:', N8N_WEBHOOK_URL);
console.log('[gemini.js] Source:', process.env.REACT_APP_N8N_WEBHOOK_URL ? 'environment' : 'fallback');

const cleanBase64 = (base64Data) => {
  if (!base64Data) {
    console.error('[cleanBase64] ERROR: null/undefined data');
    return '';
  }
  const cleaned = base64Data.split(',')[1] || base64Data;
  console.log('[cleanBase64]', (base64Data.length / 1024).toFixed(0), 'KB ->', (cleaned.length / 1024).toFixed(0), 'KB');
  return cleaned;
};

/**
 * Generate styled image via n8n webhook
 * Optimized to use URLs instead of base64 for faster transfer
 * @param {string} userPhoto - User's photo (URL or base64)
 * @param {string} stylePrompt - AI prompt for styling
 * @param {string} generationMode - 'environment' or 'transformation'
 * @param {boolean} isUserPhotoUrl - Whether userPhoto is a URL (true) or base64 (false)
 */
export const generateStyledImage = async (userPhoto, stylePrompt, generationMode = 'transformation', isUserPhotoUrl = false) => {
  console.log('========== IMAGE GENERATION START ==========');
  console.log('[GEN] URL:', N8N_WEBHOOK_URL);
  console.log('[GEN] Mode:', generationMode);
  console.log('[GEN] User photo:', userPhoto ? (isUserPhotoUrl ? 'URL' : `base64 (${(userPhoto.length / 1024).toFixed(0)} KB)`) : 'MISSING');

  try {
    if (!userPhoto) throw new Error('User photo required');
    if (!stylePrompt) throw new Error('Prompt required');

    // Build payload - URLs or base64 with data prefix (same field names for both)
    let userPhotoPayload;

    if (isUserPhotoUrl) {
      // OPTIMIZED: User photo is already a URL
      userPhotoPayload = userPhoto;
      console.log('[STEP 1] User photo: URL');
    } else {
      // FALLBACK: User photo is base64 - ensure it has data URL prefix
      userPhotoPayload = userPhoto.startsWith('data:')
        ? userPhoto
        : 'data:image/jpeg;base64,' + cleanBase64(userPhoto);
      console.log('[STEP 1] User photo: base64 with data prefix');
    }

    const payload = {
      userPhoto: userPhotoPayload,
      stylePrompt: stylePrompt,
      generationMode: generationMode,
      timestamp: new Date().toISOString()
    };

    const payloadSize = JSON.stringify(payload).length;
    console.log('[STEP 1] Payload size:', (payloadSize / 1024).toFixed(1) + ' KB');

    console.log('[STEP 2] Sending POST to:', N8N_WEBHOOK_URL);
    const startTime = Date.now();
    let response;
    try {
      response = await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const elapsedTime = ((Date.now() - startTime) / 1000).toFixed(2);
      console.log('[STEP 2] Response received:', response.status, '(took', elapsedTime, 'seconds)');
    } catch (fetchError) {
      console.error('[STEP 2] FAILED:', fetchError.message);
      throw fetchError;
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[STEP 2] HTTP Error Response:', errorText);
      throw new Error('HTTP ' + response.status + ': ' + errorText.substring(0, 200));
    }

    console.log('[STEP 3] Parsing response...');
    const responseText = await response.text();
    console.log('[STEP 3] Raw response (first 500 chars):', responseText.substring(0, 500));

    let result;
    try {
      result = JSON.parse(responseText);
    } catch (jsonError) {
      console.error('[STEP 3] JSON Parse Error:', jsonError.message);
      throw new Error('Failed to parse response as JSON: ' + jsonError.message);
    }
    console.log('[STEP 3] Response received:', {
      hasGeneratedImage: !!result.generatedImage,
      responseKeys: Object.keys(result)
    });

    // Check for generatedImage field
    if (!result.generatedImage) {
      console.error('[STEP 3] ERROR: No generatedImage field in response');
      throw new Error('No image in response. Response structure: ' + JSON.stringify(Object.keys(result)));
    }

    const generatedImage = result.generatedImage;

    // Ensure base64 has proper data URL prefix
    const imageData = generatedImage.startsWith('data:')
      ? generatedImage
      : 'data:image/jpeg;base64,' + generatedImage;

    console.log('[STEP 3] Image received as base64, size:', (imageData.length / 1024).toFixed(0), 'KB');
    console.log('========== GENERATION SUCCESS ==========');
    return {
      imageData: imageData
    };

  } catch (error) {
    console.error('========== GENERATION FAILED ==========');
    console.error('[ERROR]:', error.message);

    if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
      throw new Error('Unable to connect to image generation service. This is likely a CORS issue. The n8n webhook needs to allow requests from your domain.');
    }
    throw new Error('Generation failed: ' + error.message);
  }
};

export const isGeminiConfigured = () => {
  return !!N8N_WEBHOOK_URL;
};
